function goldenNovaActive() {
  return typeof window.baCurr === 'object' && window.baCurr !== null && typeof window.baCurr.refreshConversion === 'function';
}

function goldenRefreshCurrency() {
  if (goldenNovaActive()) window.baCurr.refreshConversion();
}

// After any dynamic price render, kick Nova's conversion and resync the currency-code chips
// (owned by currency-duties-2026-07.js) without hard-depending on either being present.
function goldenSyncCurrency() {
  goldenRefreshCurrency();
  if (typeof window.amoreUpdateCurrencyCodes === 'function') window.amoreUpdateCurrencyCodes();
}

// Wraps an already shop-formatted amount (from the `money` filter, or goldenFormatRawMoney) in
// the same markup Nova scans for. Labeling is entirely the [data-amore-currency-code] chip's
// job now (see currency-duties-2026-07.js) — this never appends a currency code itself, and
// never wraps a value that already contains markup (a `.money` span, or any element), so a
// value that has already been through goldenMoneyHtml/the `money` filter can safely be passed
// through this again without producing a nested `.money .money`.
function goldenMoneyHtml(shopFormattedAmount) {
  const value = String(shopFormattedAmount == null ? '' : shopFormattedAmount);
  const probe = document.createElement('template');
  probe.innerHTML = value;
  if (probe.content.querySelector('span, .money')) return value;
  return `<span class="money">${value}</span>`;
}

// Sets a price container's HTML while preserving its existing [data-amore-currency-code] chip
// (a full innerHTML replace would otherwise delete it, since it lives inside the container in
// the Liquid markup) instead of creating a second one.
function goldenSetPriceHtml(container, html) {
  if (!container) return;
  const chip = container.querySelector('[data-amore-currency-code]');
  container.innerHTML = html;
  if (chip && !container.querySelector('[data-amore-currency-code]')) {
    container.appendChild(chip);
  }
}

// The shop's configured money format, for values with no server-rendered string (e.g. a
// live-computed CTL total). Comes from the CTL section, which carries `shop.money_format` with
// its HTML stripped in Liquid. window.Shopify.money_format is only a fallback: Shopify does not
// define it on the storefront, and the third-party app that used to has stopped emitting it.
function goldenMoneyFormat() {
  const section = document.querySelector('[data-golden-ctl]');
  return (section && section.dataset.moneyFormat) || (window.Shopify && window.Shopify.money_format) || '';
}

// Shopify's placeholder names describe the DECIMAL mark, not the thousands one: an
// `_with_comma_separator` format renders 1.134,65. Anything unqualified groups with a comma.
const GOLDEN_MONEY_SEPARATORS = {
  comma: { group: '.', decimal: ',' },
  space: { group: ' ', decimal: ',' },
  apostrophe: { group: "'", decimal: '.' },
  default: { group: ',', decimal: '.' },
};

// Raw cents as a bare grouped number, with no currency symbol around it.
function goldenFormatRawAmount(rawCents) {
  const placeholder = goldenMoneyFormat().match(/\{\{\s*(amount[a-z_]*)\s*\}\}/);
  // No format reached us: group with spaces and drop the decimal tail. Nova re-parses whatever
  // we render and reads a "," or a "." as a thousands group, so a ",00" tail would make every
  // converted price 100x too big -- do not "simplify" this back to a decimal-bearing default.
  if (!placeholder) return String(Math.round(Number(rawCents) / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const name = placeholder[1];
  const separators = GOLDEN_MONEY_SEPARATORS[(name.match(/_with_(comma|space|apostrophe)_separator/) || [])[1] || 'default'];
  const amount = Number(rawCents) / 100;
  const fixed = /_no_decimals/.test(name) ? String(Math.round(amount)) : amount.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separators.group);
  return decPart ? `${grouped}${separators.decimal}${decPart}` : grouped;
}

// The same amount wrapped in the shop's currency symbol, i.e. what `{{ price | money }}` would
// have rendered server-side, so a computed total reads identically to the line prices above it.
function goldenFormatRawMoney(rawCents) {
  const template = goldenMoneyFormat();
  const placeholder = template.match(/\{\{\s*amount[a-z_]*\s*\}\}/);
  const amount = goldenFormatRawAmount(rawCents);
  return placeholder ? template.replace(placeholder[0], amount) : amount;
}

// Plain-text IDR line mirroring the main-price `amore-price-row__idr` pattern. Deliberately
// NOT wrapped in a `.money` span / goldenMoneyHtml so the Nova currency app leaves it as the
// raw store-currency (IDR) amount instead of converting it to the shopper's display currency.
// Uses the bare amount: the line writes its own "IDR" label, so the symbol would double up.
// Formats from the number, NOT from goldenFormatRawAmount's shop-formatted string: this line is
// always whole IDR with comma groups regardless of what money format the shop is set to. Reusing
// the shop format here would turn a decimal tail (",00" / ".00") into a fourth group the moment
// someone switches the store off `amount_no_decimals_with_space_separator`. Truncates rather than
// rounds — a fractional rupiah is never actually charged.
function goldenIdrLine(rawCents) {
  const rupiah = Math.trunc(Number(rawCents) / 100);
  if (!Number.isFinite(rupiah)) return '';
  return 'IDR ' + String(rupiah).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' charged';
}

if (!customElements.get('golden-pdp')) {
  customElements.define(
    'golden-pdp',
    class GoldenPdp extends HTMLElement {
      constructor() {
        super();
        this.ready = this.dataset.ready === 'true';
        this.variants = this.readJson('[data-golden-variants]', []);
        this.productPriceFormatted = this.dataset.productPriceFormatted || '';
        this.selectedVariant = this.variantById(this.dataset.selectedVariantId);
        this.sizeGroup = this.querySelector('[data-golden-size-group]');
        this.sizeError = this.querySelector('[data-golden-size-error]');
        this.productForm = this.querySelector('product-form');
        this.form = this.productForm?.querySelector('form');
        this.variantInput = this.form?.querySelector('[name="id"]');
        this.submit = this.querySelector('[data-golden-submit]');
        this.submitLabel = this.querySelector('[data-golden-submit-label]');
        this.status = this.querySelector('[data-golden-status]');
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        try {
          this.bindVariantPicker();
          this.validateInitialVariantUrl();
          this.bindGallery();
          this.bindZoom();
          this.bindMeasurementUnits();
          this.bindStickyDock();
          this.bindSharedShellAccessibility();
          window.queueMicrotask(() => {
            if (!this.isConnected) return;
            this.bindCompleteLook();
            this.activateEnhancement();
          });
        } catch (_error) {
          this.deactivateEnhancement();
        }
      }

      disconnectedCallback() {
        if (this.boundPopState) window.removeEventListener('popstate', this.boundPopState);
        if (this.boundStickyUpdate) {
          window.removeEventListener('scroll', this.boundStickyUpdate);
          window.removeEventListener('resize', this.boundStickyUpdate);
        }
        if (this.galleryScrollFrame) window.cancelAnimationFrame(this.galleryScrollFrame);
        if (this.shellObserver) this.shellObserver.disconnect();
        if (this.boundShellLoad) window.removeEventListener('load', this.boundShellLoad);
      }

      activateEnhancement() {
        this.querySelectorAll('[data-golden-baseline]').forEach((element) => {
          element.hidden = true;
        });
        this.querySelectorAll('[data-golden-enhanced]').forEach((element) => {
          element.hidden = false;
        });
        this.classList.add('is-enhanced');
        this.updateSticky();
      }

      deactivateEnhancement() {
        if (this.hydrateGallery) this.hydrateGallery();
        this.classList.remove('is-enhanced');
        this.querySelectorAll('[data-golden-enhanced]').forEach((element) => {
          element.hidden = true;
        });
        this.querySelectorAll('[data-golden-baseline]').forEach((element) => {
          element.hidden = false;
        });
      }

      readJson(selector, fallback) {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent || '');
        } catch (_error) {
          return fallback;
        }
      }

      variantById(id) {
        if (!id) return null;
        return this.variants.find((variant) => String(variant.id) === String(id)) || null;
      }

      bindVariantPicker() {
        this.querySelectorAll('[name^="golden-size-"]').forEach((input) => {
          input.addEventListener('change', () => {
            const variant = this.variantById(input.value);
            if (!variant) return;
            this.applyVariant(variant, { updateUrl: true, announce: true });
          });
        });

        this.boundPopState = () => {
          const requested = new URL(window.location.href).searchParams.get('variant');
          this.applyVariant(this.variantById(requested), { updateUrl: false, announce: false });
        };
        window.addEventListener('popstate', this.boundPopState);
      }

      validateInitialVariantUrl() {
        const requested = new URL(window.location.href).searchParams.get('variant');
        if (requested && !this.variantById(requested)) {
          this.applyVariant(null, { updateUrl: false, announce: false });
          this.showSizeError('This size link is unavailable.');
        }
      }

      applyVariant(variant, { updateUrl, announce }) {
        this.selectedVariant = variant;
        this.dataset.selectedVariantId = variant?.id || '';

        this.querySelectorAll('[name^="golden-size-"]').forEach((input) => {
          input.checked = Boolean(variant && String(input.value) === String(variant.id));
        });

        if (this.variantInput) {
          this.variantInput.value = variant?.id || '';
          this.variantInput.disabled = !(this.ready && variant?.available);
        }

        if (variant && !variant.available) {
          this.showSizeError('This size link is unavailable.');
        } else {
          this.hideSizeError();
        }

        if (this.submit) {
          this.submit.disabled = !(this.ready && variant?.available);
          this.submit.removeAttribute('aria-disabled');
          this.submit.classList.toggle('golden-pdp__primary-button--pending', this.ready && !variant);
        }

        if (this.submitLabel) {
          if (!this.ready) {
            this.submitLabel.textContent = 'Temporarily unavailable';
          } else if (!variant) {
            this.submitLabel.textContent = 'Choose a size';
          } else if (!variant.available) {
            this.submitLabel.textContent = 'Size unavailable';
          } else {
            this.submitLabel.innerHTML = `Add to bag · ${goldenMoneyHtml(this.escapeHtml(variant.priceFormatted))}`;
          }
        }

        const price = this.querySelector('[data-golden-price]');
        if (price) {
          if (variant) {
            goldenSetPriceHtml(
              price,
              variant.compareAtPrice > variant.price
                ? `<s>${goldenMoneyHtml(this.escapeHtml(variant.compareAtPriceFormatted))}</s>${goldenMoneyHtml(
                    this.escapeHtml(variant.priceFormatted)
                  )}`
                : goldenMoneyHtml(this.escapeHtml(variant.priceFormatted))
            );
          } else {
            goldenSetPriceHtml(price, goldenMoneyHtml(this.escapeHtml(this.productPriceFormatted)));
          }
        }

        this.updateSticky();
        this.updateCtlAnchor();
        goldenSyncCurrency();

        if (updateUrl && variant) {
          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', nextUrl);
        }

        if (announce && this.status) {
          this.status.textContent = variant.available
            ? `Size ${variant.title} selected.`
            : `Size ${variant.title} is unavailable.`;
        }
      }

      showSizeError(message) {
        if (!this.sizeError) return;
        this.sizeError.textContent = message;
        this.sizeError.classList.remove('hidden');
      }

      hideSizeError() {
        this.sizeError?.classList.add('hidden');
      }

      bindGallery() {
        const gallery = this.querySelector('[data-golden-gallery]');
        const track = gallery?.querySelector('[data-golden-gallery-track]');
        if (!gallery || !track) return;

        const media = Array.from(track.querySelectorAll('[data-media-index]'));
        const current = gallery.querySelector('[data-golden-gallery-current]');
        const thumbnails = Array.from(gallery.querySelectorAll('[data-golden-thumbnail]'));
        let activeIndex = 0;

        /* CC-24: frames 2-9 ship without src (see golden-pdp-gallery.liquid).
           Hydrate them on the first gallery interaction; on desktop the grid
           shows every frame, so hydrate as frames approach the viewport. */
        const hydrateFrame = (img) => {
          if (!img || !img.dataset.goldenDeferredSrc) return;
          if (img.dataset.goldenDeferredSizes) img.sizes = img.dataset.goldenDeferredSizes;
          if (img.dataset.goldenDeferredSrcset) img.srcset = img.dataset.goldenDeferredSrcset;
          img.src = img.dataset.goldenDeferredSrc;
          img.removeAttribute('data-golden-deferred-src');
        };
        const deferredImages = Array.from(track.querySelectorAll('img[data-golden-deferred-src]'));
        this.hydrateGallery = () => deferredImages.forEach(hydrateFrame);

        ['pointerdown', 'touchstart', 'keydown'].forEach((eventName) => {
          gallery.addEventListener(eventName, this.hydrateGallery, { passive: true, once: true });
        });

        if (window.matchMedia('(min-width: 1200px)').matches && 'IntersectionObserver' in window) {
          const frameObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                frameObserver.unobserve(entry.target);
                hydrateFrame(entry.target);
              });
            },
            { rootMargin: '600px 0px' }
          );
          deferredImages.forEach((img) => frameObserver.observe(img));
        }

        const setActive = (index, shouldScroll = false) => {
          activeIndex = Math.max(0, Math.min(index, media.length - 1));
          media.forEach((item, itemIndex) => item.classList.toggle('is-current', itemIndex === activeIndex));
          thumbnails.forEach((button, buttonIndex) => {
            button.toggleAttribute('aria-current', buttonIndex === activeIndex);
          });
          if (current) current.textContent = String(activeIndex + 1);
          if (shouldScroll) {
            media[activeIndex]?.scrollIntoView({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              block: 'nearest',
              inline: 'start',
            });
          }
        };

        gallery.querySelector('[data-golden-gallery-prev]')?.addEventListener('click', () => {
          setActive(activeIndex > 0 ? activeIndex - 1 : media.length - 1, true);
        });
        gallery.querySelector('[data-golden-gallery-next]')?.addEventListener('click', () => {
          setActive(activeIndex < media.length - 1 ? activeIndex + 1 : 0, true);
        });
        thumbnails.forEach((button) => {
          button.addEventListener('click', () => setActive(Number(button.dataset.goldenThumbnail), true));
        });

        track.addEventListener(
          'scroll',
          () => {
            this.hydrateGallery();
            window.cancelAnimationFrame(this.galleryScrollFrame);
            this.galleryScrollFrame = window.requestAnimationFrame(() => {
              if (window.innerWidth >= 1200) return;
              const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
              setActive(index);
            });
          },
          { passive: true }
        );
      }

      bindZoom() {
        const dialog = this.querySelector('[data-golden-zoom-dialog]');
        const content = dialog?.querySelector('[data-golden-zoom-content]');
        if (!dialog || !content || typeof dialog.showModal !== 'function') return;
        let opener;

        this.querySelectorAll('[data-golden-zoom-open]').forEach((button) => {
          button.addEventListener('click', () => {
            const figure = button.closest('[data-media-id]');
            const image = figure?.querySelector('img');
            if (!image) return;
            opener = button;
            content.replaceChildren(image.cloneNode(true));
            dialog.showModal();
            dialog.querySelector('[data-golden-zoom-close]')?.focus();
          });
        });

        const close = () => {
          dialog.close();
          content.replaceChildren();
          opener?.focus();
        };

        dialog.querySelector('[data-golden-zoom-close]')?.addEventListener('click', close);
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) close();
        });
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          close();
        });
      }

      bindMeasurementUnits() {
        this.querySelectorAll('[data-golden-unit]').forEach((button) => {
          button.addEventListener('click', () => {
            const unit = button.dataset.goldenUnit;
            this.querySelectorAll('[data-golden-unit]').forEach((candidate) => {
              candidate.setAttribute('aria-pressed', String(candidate === button));
            });
            this.querySelectorAll('[data-cm]').forEach((cell) => {
              const value = Number(cell.dataset.cm);
              cell.textContent = unit === 'cm' ? `${this.trimNumber(value)} cm` : `${this.trimNumber(value / 2.54)} in`;
            });
            this.querySelectorAll('[data-cm-min][data-cm-max]').forEach((cell) => {
              const minimum = Number(cell.dataset.cmMin);
              const maximum = Number(cell.dataset.cmMax);
              cell.textContent =
                unit === 'cm'
                  ? `${this.trimNumber(minimum)}–${this.trimNumber(maximum)} cm`
                  : `${this.trimNumber(minimum / 2.54)}–${this.trimNumber(maximum / 2.54)} in`;
            });
          });
        });
      }

      bindStickyDock() {
        this.sticky = this.querySelector('[data-golden-sticky]');
        this.stickyAction = this.sticky?.querySelector('[data-golden-sticky-action]');
        if (!this.sticky || !this.stickyAction || !this.ready) return;

        this.stickyAction.addEventListener('click', () => {
          if (this.selectedVariant?.available) {
            this.submit?.click();
            return;
          }
          this.sizeGroup?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'center',
          });
          window.setTimeout(() => this.sizeGroup?.querySelector('legend')?.focus(), 220);
        });

        this.boundStickyUpdate = () => this.updateSticky();
        window.addEventListener('scroll', this.boundStickyUpdate, { passive: true });
        window.addEventListener('resize', this.boundStickyUpdate, { passive: true });
        this.updateSticky();
      }

      bindSharedShellAccessibility() {
        const normalize = () => {
          const wishlist = document.getElementById('dawn-desktop');
          if (wishlist) {
            if (wishlist.getAttribute('aria-label') !== 'Open Wishlist') {
              wishlist.setAttribute('aria-label', 'Open Wishlist');
            }
            wishlist.querySelectorAll('.swym-header-launcher-icon').forEach((icon) => {
              if (icon.hasAttribute('tabindex')) icon.removeAttribute('tabindex');
              if (icon.hasAttribute('role')) icon.removeAttribute('role');
              if (icon.hasAttribute('aria-label')) icon.removeAttribute('aria-label');
              if (icon.getAttribute('aria-hidden') !== 'true') icon.setAttribute('aria-hidden', 'true');
            });
          }

          document
            .querySelectorAll(
              '.footer .footer-block__heading, .footer .field__input, .footer .field__label, .footer #FooterCountryLabel'
            )
            .forEach((element) => {
              element.style.setProperty('color', '#171714', 'important');
              element.style.setProperty('-webkit-text-fill-color', '#171714', 'important');
            });
        };

        normalize();
        this.boundShellLoad = normalize;
        window.addEventListener('load', this.boundShellLoad, { once: true });

        const shell = document.querySelector('.header__icons');
        if (shell) {
          this.shellObserver = new MutationObserver(normalize);
          this.shellObserver.observe(shell, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-label', 'aria-hidden', 'role', 'tabindex'],
          });
        }
      }

      updateSticky() {
        if (!this.sticky || !this.ready || !this.classList.contains('is-enhanced')) return;
        const formRect = this.productForm?.getBoundingClientRect();
        const sizeRect = this.sizeGroup?.getBoundingClientRect();
        const controlsVisible =
          (formRect && formRect.bottom > 0 && formRect.top < window.innerHeight) ||
          (sizeRect && sizeRect.bottom > 0 && sizeRect.top < window.innerHeight);
        const enoughJourney = window.scrollY > window.innerHeight * 0.55;
        this.sticky.classList.toggle('is-visible', Boolean(enoughJourney && !controlsVisible));

        const selection = this.sticky.querySelector('[data-golden-sticky-selection]');
        if (selection) {
          selection.textContent = this.selectedVariant ? `Size ${this.selectedVariant.title}` : 'Size not selected';
        }
        if (this.stickyAction) {
          this.stickyAction.textContent = this.selectedVariant?.available ? 'Add to bag' : 'Select size';
        }
      }

      updateCtlAnchor() {
        const ctl = this.querySelector('[data-golden-ctl]');
        if (!ctl) return;
        const anchorChanged = String(ctl.dataset.anchorVariantId || '') !== String(this.selectedVariant?.id || '');
        const anchorTitle = ctl.dataset.anchorProductTitle || this.querySelector('h1')?.textContent.trim() || 'this piece';
        ctl.dataset.anchorVariantId = this.selectedVariant?.id || '';
        ctl.dataset.anchorVariantPrice = this.selectedVariant?.price || '';
        const size = ctl.querySelector('[data-golden-ctl-anchor-size]');
        const price = ctl.querySelector('[data-golden-ctl-anchor-price]');
        if (size) {
          size.textContent = this.selectedVariant
            ? `Size ${this.selectedVariant.title}`
            : `Choose your ${anchorTitle} size above`;
        }
        if (price) {
          price.innerHTML = this.selectedVariant ? goldenMoneyHtml(this.escapeHtml(this.selectedVariant.priceFormatted)) : '';
        }
        const anchorIdr = ctl.querySelector('[data-golden-ctl-anchor-idr]');
        if (anchorIdr) anchorIdr.textContent = this.selectedVariant ? goldenIdrLine(this.selectedVariant.price) : '';
        if (anchorChanged) ctl.goldenController?.resetCompletion();
        ctl.goldenController?.refresh();
      }

      bindCompleteLook() {
        const ctl = this.querySelector('[data-golden-ctl]');
        if (!ctl) return;
        const controller = new GoldenCompleteLook(ctl, this);
        ctl.goldenController = controller;
        controller.connect();
      }

      trimNumber(value) {
        return Number.isFinite(value) ? String(Math.round(value * 10) / 10) : '—';
      }

      escapeHtml(value) {
        const span = document.createElement('span');
        span.textContent = String(value);
        return span.innerHTML;
      }
    }
  );
}

class GoldenCompleteLook {
  constructor(element, pdp) {
    this.element = element;
    this.pdp = pdp;
    this.items = Array.from(element.querySelectorAll('[data-golden-ctl-item]'));
    this.submit = element.querySelector('[data-golden-ctl-submit]');
    this.submitLabel = element.querySelector('[data-golden-ctl-submit-label]');
    this.total = element.querySelector('[data-golden-ctl-total]');
    this.hint = element.querySelector('[data-golden-ctl-hint]');
    this.message = element.querySelector('[data-golden-ctl-message]');
    this.currency = element.dataset.currency;
    this.busy = false;
    this.completed = false;
    this.blocked = false;
    this.blockedLabel = '';
  }

  connect() {
    this.items.forEach((item) => {
      const include = item.querySelector('[data-golden-ctl-include]');
      const sizeInputs = Array.from(item.querySelectorAll('[data-golden-ctl-size]'));
      sizeInputs.forEach((input) => {
        input.dataset.goldenInitiallyDisabled = String(input.disabled);
      });
      include?.addEventListener('change', () => {
        this.resetCompletion();
        sizeInputs.forEach((input) => {
          input.disabled = include.checked ? input.dataset.goldenInitiallyDisabled === 'true' : true;
        });
        this.refresh();
      });
      sizeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.clearRuntimeConflict(item);
          this.resetCompletion();
          this.refresh();
        });
      });
    });
    this.submit?.addEventListener('click', () => this.addSelectedLook());
    this.refresh();
  }

  selectedCompanions() {
    return this.items
      .filter((item) => item.querySelector('[data-golden-ctl-include]')?.checked)
      .map((item) => {
        const input = item.querySelector('[data-golden-ctl-size]:checked');
        return {
          item,
          productId: item.dataset.productId,
          productTitle: item.dataset.productTitle,
          productUrl: item.dataset.productUrl,
          variantId: input?.value || '',
          variantTitle: input?.dataset.variantTitle || '',
          price: Number(input?.dataset.price || 0),
          priceFormatted: input?.dataset.priceFormatted || '',
          input,
        };
      });
  }

  clearRuntimeConflict(item) {
    item.querySelectorAll('[data-golden-ctl-size]').forEach((input) => {
      input.removeAttribute('aria-invalid');
      delete input.dataset.runtimeUnavailable;
    });
  }

  refresh() {
    const anchorId = this.element.dataset.anchorVariantId;
    const anchorBasePrice = Number(this.element.dataset.anchorPrice || 0);
    const companions = this.selectedCompanions();
    const complete = Boolean(anchorId && companions.length && companions.every((item) => item.variantId));
    if (this.submit) this.submit.disabled = !complete || this.busy || this.completed || this.blocked;

    if (this.hint) {
      this.hint.hidden = complete || this.busy || this.completed || this.blocked;
    }

    // Total always reflects the full price of every required piece in the pairing, not just
    // the ones with a size picked yet -- a shopper deciding on the pair needs the real number
    // up front, and it must not move as sizes are chosen (only unchecking "Include" changes it).
    const includedItems = this.items.filter((item) => item.querySelector('[data-golden-ctl-include]')?.checked);
    const totalPrice =
      anchorBasePrice + includedItems.reduce((sum, item) => sum + Number(item.dataset.companionPrice || 0), 0);
    if (this.total) {
      this.total.innerHTML = totalPrice > 0 ? goldenMoneyHtml(goldenFormatRawMoney(totalPrice)) : '—';
    }
    const totalIdr = this.element.querySelector('[data-golden-ctl-total-idr]');
    if (totalIdr) totalIdr.textContent = totalPrice > 0 ? goldenIdrLine(totalPrice) : '';

    // CC-29B: shipping is unconditionally free, so the note no longer depends on a threshold —
    // it used to compute "Add X for free shipping (over Y)" from freeShippingThresholdCents.
    const shippingNote = this.element.querySelector('[data-golden-ctl-shipping-note]');
    if (shippingNote) {
      shippingNote.textContent = totalPrice > 0 ? 'This pairing qualifies for free shipping.' : '';
    }

    goldenSyncCurrency();
  }

  async addSelectedLook() {
    if (this.busy) return;
    const anchorId = this.element.dataset.anchorVariantId;
    const companions = this.selectedCompanions();
    if (!anchorId) {
      const anchorTitle = this.element.dataset.anchorProductTitle || 'this piece';
      this.showMessage(`Choose your ${anchorTitle} size above before adding the look.`);
      this.pdp.sizeGroup?.scrollIntoView({ block: 'center' });
      this.pdp.sizeGroup?.querySelector('legend')?.focus();
      return;
    }
    if (!companions.length || companions.some((item) => !item.variantId)) {
      this.showMessage('Choose a size for every included piece before adding the look.');
      companions
        .find((item) => !item.variantId)
        ?.item.querySelector('[data-golden-ctl-size-group] legend')
        ?.focus();
      return;
    }

    this.setBusy(true, 'checking');
    this.hideMessage();
    const requestedItems = [
      {
        id: Number(anchorId),
        quantity: 1,
        label: `${this.pdp.querySelector('h1')?.textContent.trim() || 'This piece'} in size ${
          this.pdp.selectedVariant?.title || ''
        }`,
      },
    ].concat(
      companions.map((item) => ({
        id: Number(item.variantId),
        quantity: 1,
        label: `${item.productTitle} in size ${item.variantTitle}`,
      }))
    );
    let cartBefore;

    try {
      const validation = await this.validateSelections(companions);
      if (!validation.ok) {
        if (validation.type === 'price') {
          this.showPriceChange(validation.changes, validation.lines);
          return;
        }
        if (['availability', 'variant', 'deleted'].includes(validation.type)) {
          this.blocked = true;
          this.blockedLabel = 'Review selection';
          this.markRuntimeConflict(validation.line);
        }
        this.showMessage(validation.message);
        validation.focusTarget?.focus();
        return;
      }

      cartBefore = await this.fetchCart();
      this.setBusy(true, 'adding');

      let addResponse;
      try {
        addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: requestedItems.map(({ id, quantity }) => ({ id, quantity })),
            sections: this.cartSections(),
            sections_url: window.location.pathname,
          }),
        });
      } catch (_networkError) {
        this.showProgress('We’re confirming whether the look was added. Please don’t retry yet.');
        return await this.reconcileAfterFailure(cartBefore, requestedItems, null, 'The network connection was interrupted.');
      }

      let addPayload = null;
      try {
        addPayload = await addResponse.json();
      } catch (_parseError) {
        // Reconciliation, not the response body, is the transaction source of truth.
      }

      if (!addResponse.ok) {
        this.showProgress('We’re confirming whether the look was added. Please don’t retry yet.');
        return await this.reconcileAfterFailure(
          cartBefore,
          requestedItems,
          addPayload,
          addPayload?.description || addPayload?.message || 'Shopify could not add the selected look.'
        );
      }

      // addResponse.ok is the server's own confirmation of the add -- Shopify's cart/add.js
      // rejects the whole batch (non-2xx) if any line fails, so a 2xx here already means every
      // requested item is in the cart. cartAfter is fetched separately (a second, independent
      // request) purely to refresh the on-page cart UI and to produce nicer per-item labels; on
      // a slow connection it can race and return a stale snapshot that looks like nothing was
      // added. That race must never be allowed to overturn a server-confirmed success into a
      // false "No item was added".
      const cartAfter = await this.fetchCart().catch(() => null);
      const analysis = cartAfter ? this.analyzeDelta(cartBefore, cartAfter, requestedItems) : null;
      const committed =
        analysis && analysis.outcome === 'full' ? analysis.committed : requestedItems.map((item) => item.label);
      this.completed = true;
      this.showMessage(`${committed.join(' and ')} were added to your bag.`, true, true);
      this.renderCart(addPayload, cartAfter || {});
      /* R3: renderCart only refreshes the header cart-icon-bubble. Also publish Dawn's
         cartUpdate so the cart DRAWER and cart NOTIFICATION re-render off the same add.
         Purely additive; typeof guards never throw and the try/catch guarantees this can
         never break the existing add flow. */
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'golden-complete-look', cartData: addPayload });
        }
      } catch (_r3Error) {
        /* R3: swallow — the completed add already succeeded and must stand. */
      }
      return;
    } catch (error) {
      if (cartBefore) {
        await this.reconcileAfterFailure(cartBefore, requestedItems, null, error.message);
      } else {
        this.showMessage('The selected look could not be verified. Nothing was submitted. Check your connection and try again.');
      }
    } finally {
      this.setBusy(false);
    }
  }

  async validateSelections(companions) {
    const anchor = {
      kind: 'anchor',
      productId: this.element.dataset.anchorProductId,
      productTitle: this.element.dataset.anchorProductTitle,
      productUrl: this.element.dataset.anchorProductUrl,
      variantId: this.element.dataset.anchorVariantId,
      variantTitle: this.pdp.selectedVariant?.title || '',
      price: Number(this.element.dataset.anchorVariantPrice || 0),
      focusTarget: this.pdp.sizeGroup?.querySelector('legend'),
    };
    const lines = [anchor].concat(
      companions.map((companion) => ({
        ...companion,
        kind: 'companion',
        focusTarget: companion.item.querySelector('[data-golden-ctl-size-group] legend'),
      }))
    );
    const priceChanges = [];
    for (const line of lines) {
      let response;
      try {
        response = await fetch(`${line.productUrl}.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
      } catch (_error) {
        return {
          ok: false,
          type: 'network',
          line,
          message: `${line.productTitle} could not be checked. Nothing was added. Check your connection and try again.`,
        };
      }
      if (response.status === 404) {
        return {
          ok: false,
          type: 'deleted',
          line,
          focusTarget: line.focusTarget,
          message: `${line.productTitle} is no longer available. Nothing was added. Review the look and decide how to continue.`,
        };
      }
      if (!response.ok) {
        return {
          ok: false,
          type: 'shopify',
          line,
          message: `${line.productTitle} could not be checked. Nothing was added. Try again when Shopify is available.`,
        };
      }
      const product = await response.json();
      const currentVariant = product.variants?.find(
        (variant) => String(variant.id) === String(line.variantId)
      );
      if (!currentVariant) {
        return {
          ok: false,
          type: 'variant',
          line,
          message: `The selected ${line.productTitle} size no longer exists. Nothing was added. Choose a current size.`,
          focusTarget: line.focusTarget,
        };
      }
      if (!currentVariant.available) {
        return {
          ok: false,
          type: 'availability',
          line,
          message: `${line.productTitle} in size ${currentVariant.title} is no longer available. Nothing was added. Choose another size.`,
          focusTarget: line.focusTarget,
        };
      }
      if (Number(currentVariant.price) !== line.price) {
        priceChanges.push({
          line,
          currentPrice: Number(currentVariant.price),
          currentVariantTitle: currentVariant.title,
        });
      }
    }
    if (priceChanges.length) return { ok: false, type: 'price', changes: priceChanges, lines };
    return { ok: true, lines };
  }

  markRuntimeConflict(line) {
    if (!line) return;
    if (line.kind === 'anchor') {
      const input = this.pdp.sizeGroup?.querySelector(`[value="${CSS.escape(String(line.variantId))}"]`);
      input?.setAttribute('aria-invalid', 'true');
      if (input) input.dataset.runtimeUnavailable = 'true';
      this.pdp.showSizeError(`${line.productTitle} in size ${line.variantTitle} is no longer available.`);
      if (this.pdp.submit) this.pdp.submit.disabled = true;
      if (this.pdp.submitLabel) this.pdp.submitLabel.textContent = 'Size unavailable';
      return;
    }
    if (line.input) {
      line.input.setAttribute('aria-invalid', 'true');
      line.input.dataset.runtimeUnavailable = 'true';
    }
  }

  showPriceChange(changes, lines) {
    this.blocked = true;
    this.blockedLabel = 'Review updated total';
    const previousTotal = lines.reduce((sum, line) => sum + line.price, 0);
    const changedById = new Map(changes.map((change) => [String(change.line.variantId), change]));
    const currentTotal = lines.reduce(
      (sum, line) => sum + (changedById.get(String(line.variantId))?.currentPrice || line.price),
      0
    );
    const changedNames = changes.map((change) => change.line.productTitle).join(', ');
    // Plain-text status message: showMessage() only accepts a text node, so it can't host a
    // live-converting money span. Label it with the shop currency instead of a bare number.
    this.showMessage(
      `The price of ${changedNames} changed. Previous total ${goldenFormatRawMoney(previousTotal)} ${
        this.currency
      }; updated total ${goldenFormatRawMoney(currentTotal)} ${
        this.currency
      }. Review the updated total before adding the look.`
    );

    const accept = document.createElement('button');
    accept.type = 'button';
    accept.textContent = 'Accept updated total';
    accept.addEventListener(
      'click',
      () => {
        changes.forEach(({ line, currentPrice }) => {
          const formatted = goldenFormatRawMoney(currentPrice);
          line.price = currentPrice;
          if (line.kind === 'anchor') {
            this.element.dataset.anchorVariantPrice = String(currentPrice);
            if (this.pdp.selectedVariant) {
              this.pdp.selectedVariant.price = currentPrice;
              this.pdp.selectedVariant.priceFormatted = formatted;
            }
            const identityPrice = this.pdp.querySelector('[data-golden-price]');
            goldenSetPriceHtml(identityPrice, goldenMoneyHtml(formatted));
            const anchorPrice = this.element.querySelector('[data-golden-ctl-anchor-price]');
            if (anchorPrice) anchorPrice.innerHTML = goldenMoneyHtml(formatted);
          } else if (line.input) {
            line.input.dataset.price = String(currentPrice);
            line.input.dataset.priceFormatted = formatted;
            const linePrice = line.item.querySelector('[data-golden-companion-price]');
            if (linePrice) linePrice.innerHTML = goldenMoneyHtml(formatted);
          }
        });
        this.blocked = false;
        this.blockedLabel = '';
        this.hideMessage();
        this.refresh();
        this.submit?.focus();
      },
      { once: true }
    );
    this.message.append(document.createTextNode(' '), accept);
    accept.focus();
  }

  async reconcileAfterFailure(cartBefore, requestedItems, response, reason) {
    try {
      const cartAfter = await this.fetchCart();
      const analysis = this.analyzeDelta(cartBefore, cartAfter, requestedItems);
      if (analysis.outcome === 'full') {
        this.completed = true;
        this.showMessage(`${analysis.committed.join(' and ')} were added to your bag.`, true, true);
        this.renderCart(response, cartAfter);
        return;
      }
      this.handleNonFullOutcome(analysis, reason);
      this.renderCart(response, cartAfter);
    } catch (_error) {
      this.blocked = true;
      this.blockedLabel = 'Review bag';
      this.showMessage(
        'Shopify could not confirm whether the look reached your bag. No retry was attempted. Review your bag before deciding what to do next.',
        false,
        true,
        true
      );
    }
  }

  handleNonFullOutcome(analysis, reason) {
    if (analysis.outcome === 'zero') {
      this.showMessage(`${reason || 'The selected look could not be added'} No item from this action was added. Your choices are preserved.`);
      return;
    }
    if (analysis.outcome === 'partial') {
      this.blocked = true;
      this.blockedLabel = 'Review bag';
      this.showMessage(
        `Only part of the look reached your bag. ${analysis.committed.join(', ')} ${
          analysis.committed.length === 1 ? 'was' : 'were'
        } added; ${analysis.missing.join(', ')} ${
          analysis.missing.length === 1 ? 'was' : 'were'
        } not confirmed. No automatic removal, replacement or retry was made.`,
        false,
        true,
        true
      );
      return;
    }
    this.blocked = true;
    this.blockedLabel = 'Review bag';
    this.showMessage(
      'The bag result could not be matched to the selected look. No automatic correction or retry was made. Review your bag before deciding what to do next.',
      false,
      true,
      true
    );
  }

  analyzeDelta(before, after, requestedItems) {
    const requestedCounts = new Map();
    requestedItems.forEach((item) => {
      requestedCounts.set(item.id, (requestedCounts.get(item.id) || 0) + item.quantity);
    });

    const beforeCounts = this.variantCounts(before);
    const afterCounts = this.variantCounts(after);
    let matched = 0;
    let zero = 0;
    const committed = [];
    const missing = [];
    let unexpected = false;

    requestedCounts.forEach((quantity, variantId) => {
      const delta = (afterCounts.get(variantId) || 0) - (beforeCounts.get(variantId) || 0);
      const label = requestedItems.find((item) => item.id === variantId)?.label || `Variant ${variantId}`;
      if (delta === quantity) {
        matched += 1;
        committed.push(label);
      } else if (delta === 0) {
        zero += 1;
        missing.push(label);
      } else {
        unexpected = true;
      }
    });

    let outcome = 'unknown';
    if (!unexpected && matched === requestedCounts.size) outcome = 'full';
    else if (!unexpected && zero === requestedCounts.size) outcome = 'zero';
    else if (!unexpected && matched + zero === requestedCounts.size) outcome = 'partial';
    return { outcome, committed, missing };
  }

  variantCounts(cart) {
    const counts = new Map();
    (cart.items || []).forEach((item) => {
      counts.set(Number(item.variant_id), (counts.get(Number(item.variant_id)) || 0) + Number(item.quantity));
    });
    return counts;
  }

  async fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('The bag could not be checked.');
    return response.json();
  }

  cartSections() {
    return ['cart-icon-bubble'];
  }

  renderCart(response, cartState) {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    const iconSection = response?.sections?.['cart-icon-bubble'];
    if (icon && iconSection) {
      const next = new DOMParser().parseFromString(iconSection, 'text/html').getElementById('cart-icon-bubble');
      if (next) {
        icon.innerHTML = next.innerHTML;
        return;
      }
    }

    const count = Number(cartState?.item_count);
    if (!Number.isFinite(count)) return;
    const bubble = icon.querySelector('.cart-count-bubble');
    if (count <= 0) {
      bubble?.remove();
      return;
    }
    const nextBubble = bubble || document.createElement('div');
    nextBubble.className = 'cart-count-bubble';
    nextBubble.replaceChildren();
    const visualCount = document.createElement('span');
    visualCount.setAttribute('aria-hidden', 'true');
    visualCount.textContent = count < 100 ? String(count) : '';
    const spokenCount = document.createElement('span');
    spokenCount.className = 'visually-hidden';
    spokenCount.textContent = `${count} items`;
    nextBubble.append(visualCount, spokenCount);
    if (!bubble) {
      icon.append(nextBubble);
    }
  }

  setBusy(busy, phase = 'checking') {
    this.busy = busy;
    this.element.querySelectorAll('[data-golden-ctl-include], [data-golden-ctl-size]').forEach((control) => {
      if (busy) {
        if (control.dataset.goldenDisabledBeforeBusy == null) {
          control.dataset.goldenDisabledBeforeBusy = String(control.disabled);
        }
        control.disabled = true;
      } else if (control.dataset.goldenDisabledBeforeBusy != null) {
        control.disabled = control.dataset.goldenDisabledBeforeBusy === 'true';
        delete control.dataset.goldenDisabledBeforeBusy;
      }
    });
    if (this.submit) {
      this.submit.disabled = busy;
      this.submit.setAttribute('aria-busy', String(busy));
    }
    if (this.submitLabel) {
      this.submitLabel.textContent = busy
        ? phase === 'adding'
          ? 'Adding selected look…'
          : 'Checking selected look…'
        : this.completed
          ? 'Added · View bag'
          : this.blocked
            ? this.blockedLabel || 'Review selection'
            : 'Add selected look';
    }
    this.refresh();
  }

  showMessage(message, success = false, includeBagLink = false, focusSummary = false) {
    if (!this.message) return;
    this.message.replaceChildren(document.createTextNode(message));
    if (includeBagLink) {
      const link = document.createElement('a');
      link.href = window.routes?.cart_url || `${window.Shopify.routes.root}cart`;
      link.textContent = 'View bag';
      this.message.append(document.createTextNode(' '), link);
    }
    this.message.hidden = false;
    this.message.classList.toggle('is-success', success);
    this.message.setAttribute('role', success ? 'status' : 'alert');
    this.message.setAttribute('aria-live', success ? 'polite' : 'assertive');
    if (focusSummary) this.message.focus({ preventScroll: true });
  }

  showProgress(message) {
    if (!this.message) return;
    this.message.replaceChildren(document.createTextNode(message));
    this.message.hidden = false;
    this.message.classList.remove('is-success');
    this.message.setAttribute('role', 'status');
    this.message.setAttribute('aria-live', 'polite');
  }

  hideMessage() {
    if (!this.message) return;
    this.message.hidden = true;
    this.message.classList.remove('is-success');
    this.message.textContent = '';
  }

  resetCompletion() {
    this.completed = false;
    this.blocked = false;
    this.blockedLabel = '';
    this.hideMessage();
    if (this.submitLabel) this.submitLabel.textContent = 'Add selected look';
  }
}
